<template>
  <v-card>
    <v-card-title :class="contractColor">
      <v-row justify="space-between" align="center">
        <v-col cols="auto">
          <span class="headline">{{ $t('scheduleRateChanges') }} </span>
        </v-col>

        <v-col>
          <ActivityFormContractHeader :contract="contract"/>
        </v-col>

        <v-col cols="auto">
          <BaseDialogActions hideRefresh/>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text v-if="initialized">
      <ContractRateChangeActivityTable
      :contract="contract"
      @changed="onActivityChanged"
      @effective-date-changed="onEffectiveDateChanged"
      @scheduled-rate-change-deleted="onScheduledChangeDeleted"
      :readonly="readonly"
      >
        <template #table-head.prepend>
          <v-row align="center">
            <v-col cols="12" md="4" lg="3" style="height: 90px">
              <ReadOnlyAttributeCard
              v-if="showReadOnlyDate"
              :label="$t('effectiveDate')"
              :value="displayDate"/>

              <DatePicker
              v-else
              :dateLabel="$t('effectiveDate')"
              :startDate="effectiveDate"
              :min="minEffectiveDate"
              :max="maxEffectiveDate"
              @date-picked="onEffectiveDateChanged"/>
            </v-col>
            <v-col cols="auto" v-if="!showReadOnlyDate" style="height: 90px" class="d-flex align-center time-picker-col">
              <TimePicker
              :label="$t('time')"
              :expandable="true"
              @time-picked="onTimeChanged"/>
            </v-col>
            <v-col cols="12" md="4" v-if="showReadOnlyDate && pendingNote">
              <v-textarea
              :label="$t('note')"
              :value="pendingNote"
              readonly
              auto-grow
              rows="1"
              outlined
              dense
              hide-details/>
            </v-col>
            <v-col cols="12" md="4" v-else-if="!showReadOnlyDate" style="height: 90px" class="d-flex align-center">
              <v-text-field
              v-model="note"
              :label="$t('noteOptional')"
              maxLength="128"
              counter="128"
              clearable
              outlined
              dense/>
            </v-col>
          </v-row>
        </template>
      </ContractRateChangeActivityTable>
    </v-card-text>

    <v-card-actions class="py-4 px-0" v-if="!scheduledRateChangeAlreadyExistsForContract">
      <v-row justify="end">
        <v-col cols="auto">
          <v-btn :class="contractColor" @click="saveClicked">
            {{ $t('schedule') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>

    <Dialog :stateId="uncoveredCombinationsDialogId" @close="uncoveredCombinations = null">
      <UncoveredLogCombinationsDialog
      v-if="uncoveredCombinations"
      :gap="uncoveredCombinations"/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { localToCompanyOffsetStartOfDay, utcToCompanyOffsetDstSensitive } from '@/utils/DateFormatter.js'
import { colorClassForContractType, uniqueDialogId } from '@/utils/componentHelpers.js'
import { expansionCoverageGapFromError } from '@/utils/ExpansionCoverage.js'
import timezones from '@/components/settings-components/company-config/timezones.json'
import {
  ScheduledRateChangeCreateRequest,
  ScheduledActivityUpdateRequest,
  ScheduledActivityCreateRequest,
  ScheduledDetailRateChangeRequest
} from '@/components/contract/scheduled-rate-change/ScheduledRateChange'
import moment from 'moment'
import { ScheduledRateChangeStatus } from '@/utils/Enumerations.js'

export default {
  name: 'ContractScheduleRateChangeForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ActivityFormContractHeader: () => import('@/components/activity/ActivityFormContractHeader.vue'),
    ContractRateChangeActivityTable: () => import('@/components/contract/scheduled-rate-change/ContractRateChangeActivityTable.vue'),
    DatePicker: () => import('@/components/helper/DatePicker.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    TimePicker: () => import('@/components/helper/TimePicker.vue'),
    ReadOnlyAttributeCard: () => import('@/components/helper/ReadOnlyAttributeCard.vue'),
    UncoveredLogCombinationsDialog: () => import('@/components/activity/rate-sheet/UncoveredLogCombinationsDialog.vue')
  },

  props: {
    contract: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    effectiveDate: null,
    effectiveTime: null,
    showTimePicker: false,
    note: '',
    activities: [],
    initialized: false,
    uncoveredCombinations: null,
    uncoveredCombinationsDialogId: uniqueDialogId('schedule-uncovered-log-combinations')
  }),

  computed: {
    ...mapGetters('activity', ['allActivities', 'activitiesByEntity']),
    ...mapGetters('scheduled-rate-change', ['contractScheduledRateChanges']),
    ...mapGetters('user', ['companyInfo']),

    scheduledRateChangeAlreadyExistsForContract () {
      return this.contract.scheduledRateChangeId !== null
    },

    showReadOnlyDate () {
      return this.scheduledRateChangeAlreadyExistsForContract
    },

    pendingNote () {
      if (!this.scheduledRateChangeAlreadyExistsForContract || !Array.isArray(this.contractScheduledRateChanges)) return null
      return this.contractScheduledRateChanges.find(src => src.status === ScheduledRateChangeStatus.Pending.value)?.note ?? null
    },

    displayDate () {
      if (this.scheduledRateChangeAlreadyExistsForContract) {
        const tz = timezones.find(tz => Number(tz.offset) === this.companyInfo.timeZoneOffset)
        const tzAbbr = tz?.name?.split('(')[1]?.split(')')[0] ?? ''
        const adjusted = utcToCompanyOffsetDstSensitive(this.effectiveDate)
        const hasTime = adjusted.hours() !== 0 || adjusted.minutes() !== 0
        const formatted = adjusted.format(hasTime ? 'MM/DD/YYYY h:mm A' : 'MM/DD/YYYY')
        return hasTime && tzAbbr ? `${formatted} ${tzAbbr}` : formatted
      }
      return moment().add(1, 'days').format('MM/DD/YYYY')
    },

    contractColor () {
      return colorClassForContractType(this.contract.type)
    },

    minEffectiveDate () {
      return this.contract.effectiveDate
        ? moment.utc(this.contract.effectiveDate).format('YYYY-MM-DD')
        : undefined
    },

    maxEffectiveDate () {
      return this.contract.expirationDate
        ? moment.utc(this.contract.expirationDate).format('YYYY-MM-DD')
        : undefined
    }
  },

  mounted () {
    if (this.scheduledRateChangeAlreadyExistsForContract) {
      this.effectiveDate = this.contractScheduledRateChanges.find(src => src.status === ScheduledRateChangeStatus.Pending.value).effectiveDate
    } else {
      this.effectiveDate = localToCompanyOffsetStartOfDay(moment().add(1, 'days').format())
    }

    this.initialized = true
  },

  methods: {
    ...mapActions('scheduled-rate-change', ['createScheduledRateChange', 'fetchScheduledRateChanges']),
    ...mapActions('dialog', ['openOrUpdateDialog']),

    async saveClicked () {
      const effectiveDate = this.buildEffectiveDateTime()

      if (!this.validate(effectiveDate)) {
        return
      }

      const deletedActivityIds = this.activities
        .filter(a => a.isRemoved === true && a.activityId != null)
        .map(a => a.activityId)

      const deletedActivityIdSet = new Set(deletedActivityIds)

      const activityRateChangeRequests = this.activities
        .filter(a => this.activityOrDetailHasBeenChanged(a) || deletedActivityIdSet.has(a.deductFromActivityId))
        .map(a => {
          const severDeduction = deletedActivityIdSet.has(a.deductFromActivityId)
          return new ScheduledActivityUpdateRequest(
            effectiveDate,
            this.contract.contractId,
            a.activityTemplate.activityTemplateId,
            a.runtimeCounterparty === null ? a.contractor.accountId : null,
            a.runtimeCounterparty,
            a.recoveryMode,
            a.advanceId,
            a.baseCost?.payBy,
            a.baseCost?.payOn,
            a.baseCost?.rate,
            a.baseCost?.rateSheet,
            a.activityDetails?.map(this.activityDetailRequestMapper).filter(i => i) || [],
            a.activityId,
            severDeduction ? null : a.deductFromActivityIdentifier,
            severDeduction ? null : a.deductFromActivityId
          )
        })

      const newActivities = this.activities
        .filter(a => a.isNewlyAdded === true && a.activityId == null)
        .map(a => {
          return new ScheduledActivityCreateRequest(
            effectiveDate,
            this.contract.contractId,
            a.activityTemplate.activityTemplateId,
            a.runtimeCounterparty === null ? a.contractor.accountId : null,
            a.runtimeCounterparty,
            a.recoveryMode,
            a.advanceId,
            a.baseCost?.payBy,
            a.baseCost?.payOn,
            a.baseCost?.rate,
            a.baseCost?.rateSheet,
            a.activityDetails?.map(this.activityDetailRequestMapper).filter(i => i) || [],
            a.deductFromActivityIdentifier,
            a.deductFromActivityId,
            a.namedRateId
          )
        })

      const rateChangeRequest = new ScheduledRateChangeCreateRequest(
        this.contract.contractId,
        effectiveDate,
        deletedActivityIds,
        activityRateChangeRequests,
        newActivities,
        this.note
      )

      try {
        await this.createScheduledRateChange(rateChangeRequest)
      } catch (error) {
        this.showUncoveredCombinations(error)
        return
      }

      await this.fetchScheduledRateChanges(this.contract.contractId)

      this.$emit('save')
    },

    showUncoveredCombinations (error) {
      const gap = expansionCoverageGapFromError(error)
      if (!gap) throw error

      this.uncoveredCombinations = gap
      this.openOrUpdateDialog({ id: this.uncoveredCombinationsDialogId, width: '900px' })
    },

    validate (effectiveDate) {
      const atLeastOneActivityChanged = this.activities.some(a => a.hasBeenChanged === true || a.isRemoved === true || a.isNewlyAdded === true)
      const atLeastOneDetailChanged = this.activities.some(a => a.activityDetails?.some(d => d.hasBeenChanged === true || d.isRemoved === true))
      const atLeastOneNewDetail = this.activities.some(a => a.activityDetails?.some(d => d.activityDetailId == null))
      if (!atLeastOneActivityChanged && !atLeastOneDetailChanged && !atLeastOneNewDetail) {
        this.setSnackError(this.$t('noChangesToSchedule'))
        return false
      }

      const selected = moment.utc(effectiveDate)
      if (this.contract.effectiveDate && selected.isBefore(moment.utc(this.contract.effectiveDate))) {
        this.setSnackError(this.$t('scheduledRateChangeBeforeContractEffectiveDate'))
        return false
      }
      if (this.contract.expirationDate && selected.isSameOrAfter(moment.utc(this.contract.expirationDate).add(1, 'day'))) {
        this.setSnackError(this.$t('scheduledRateChangeAfterContractExpirationDate'))
        return false
      }
      return true
    },

    buildEffectiveDateTime () {
      const time = this.showTimePicker && this.effectiveTime ? this.effectiveTime : '00:00'
      return this.buildCompanyDateTime(this.effectiveDate, time)
    },

    activityDetailRequestMapper (detail) {
      return detail.isRemoved // TODO MK We currently don't support removing details in schedule mode
        ? null
        : new ScheduledDetailRateChangeRequest(
          detail.product.productId,
        detail.cost?.rate
        )
    },

    onActivityChanged (updatedActivities) {
      this.activities = updatedActivities
    },

    onEffectiveDateChanged (newDate) {
      if (this.scheduledRateChangeAlreadyExistsForContract) {
        this.effectiveDate = newDate
      } else {
        const dateString = moment(newDate).format()
        this.effectiveDate = localToCompanyOffsetStartOfDay(dateString)
      }
    },

    onTimeChanged (newTime) {
      this.effectiveTime = newTime
      this.showTimePicker = true
    },

    buildCompanyDateTime (effectiveDate, timeString) {
      const [hours, minutes] = timeString.split(':').map(Number)
      const companyOffset = this.companyInfo.timeZoneOffset
      const datePortion = moment.utc(effectiveDate).add(companyOffset, 'hours').format('YYYY-MM-DD')
      let offset = companyOffset
      if (moment(datePortion).isDST()) {
        offset = companyOffset + 1
      }
      return moment.utc(`${datePortion}T00:00:00`)
        .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
        .add(offset * -1, 'hours')
        .utc()
        .format()
    },

    onScheduledChangeDeleted () {
      this.$emit('scheduled-rate-change-deleted')
    },

    activityOrDetailHasBeenChanged (activity) {
      const activityChanged = activity.hasBeenChanged === true && activity.isNewlyAdded !== true
      const detailChanged = activity.activityDetails?.some(d => d.hasBeenChanged === true || (d.isNewlyAdded === true && activity.isNewlyAdded !== true))
      return activityChanged || detailChanged
    }
  }
}

</script>

<style>
.time-picker-col .mt-5 {
  margin-top: 0 !important;
}
</style>
